import React, { useCallback } from 'react';
import type { FC } from 'react';
import { isMobile } from 'react-device-detect';
import logo from '@presentation/assets/logo.png';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import Container from '@mui/material/Container';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import Typography from '@mui/material/Typography';
import { Trans, useTranslation } from 'react-i18next';
import { useTheme } from '@mui/material/styles';
import { LINK_SINGULAR } from '@main/config/links';
import { SmartBanner } from '@presentation/components/SmartBanner/SmartBanner';
import { useSmartBanner } from '@presentation/context/SmartBannerContextProvider';
import { useDownloadApp } from '@presentation/hooks/useDownloadApp';
import { useNavigate } from 'react-router-dom';
import { PlayInAppButton } from '@presentation/components/PlayInAppButton';

import { HeaderToolbar, Logo, MenuButton, MobileMenuItem, Separator, Wrapper } from './styles';

const pages = [
	{
		key: 'general.menu.categories',
		path: '/categories',
	},
	{
		key: 'general.menu.creators',
		path: '/creators',
	},
	{
		key: 'general.menu.benefits',
		path: '/benefits',
	},
];

export const Header: FC = () => {
	const { t } = useTranslation();
	const { isOpen } = useSmartBanner();
	const { openDownloadModal } = useDownloadApp();
	const navigate = useNavigate();

	const theme = useTheme();

	const [anchorElNav, setAnchorElNav] = React.useState<null | HTMLElement>(null);

	const handleOpenNavMenu = useCallback((event: React.MouseEvent<HTMLElement>) => {
		setAnchorElNav(event.currentTarget);
	}, []);

	const handleCloseNavMenu = useCallback(() => {
		setAnchorElNav(null);
	}, []);

	const downloadApp = useCallback(() => {
		if (isMobile) {
			window.open(LINK_SINGULAR);
			return;
		}

		openDownloadModal();
	}, [openDownloadModal]);

	return (
		<>
			<Wrapper isSmartBannerOpen={isOpen} position='fixed' color='transparent'>
				<SmartBanner />
				<Container maxWidth='xl'>
					<HeaderToolbar disableGutters>
						<a href='/'>
							<Logo src={logo} alt={t('general.header.logoAlt')} />
						</a>

						{/* [START] Mobile menu*/}
						<Box sx={{ flexGrow: 0, display: { xs: 'flex', md: 'none' }, marginRight: 0 }}>
							<IconButton
								size='large'
								aria-label='account of current user'
								aria-controls='menu-appbar'
								aria-haspopup='true'
								onClick={anchorElNav ? handleCloseNavMenu : handleOpenNavMenu}
								color='inherit'
							>
								{anchorElNav ? <CloseIcon /> : <MenuIcon />}
							</IconButton>
							<Menu
								id='menu-appbar'
								anchorEl={anchorElNav}
								anchorOrigin={{
									vertical: 'bottom',
									horizontal: 'left',
								}}
								keepMounted
								transformOrigin={{
									vertical: 'top',
									horizontal: 'left',
								}}
								anchorPosition={{
									top: isOpen ? theme.sizes.headerWidthSmartBanner.md : theme.sizes.header.md,
									left: 0,
								}}
								anchorReference='anchorPosition'
								marginThreshold={0}
								slotProps={{
									paper: {
										sx: {
											padding: `${theme.padding.big}px ${theme.padding.small}px`,
											width: '100vw',
											maxWidth: '100vw',
											left: '0',
											right: '0',
										},
									},
								}}
								open={!!anchorElNav}
								onClose={handleCloseNavMenu}
								MenuListProps={{ sx: { py: 0 } }}
								sx={{
									display: { xs: 'block', md: 'none' },
								}}
							>
								<PlayInAppButton fullWidth size='medium' onClick={downloadApp} />

								{pages.map((page) => (
									<MobileMenuItem
										key={page.key}
										onClick={() => {
											navigate(page.path);
											window.location.href = page.path;
											handleCloseNavMenu();
										}}
									>
										<Typography sx={{ fontSize: 18, fontWeight: 500 }}>
											<Trans i18nKey={page.key} />
										</Typography>
									</MobileMenuItem>
								))}
							</Menu>
						</Box>
						{/* [END] Mobile menu*/}

						<Box sx={{ flexGrow: 0, display: { xs: 'none', md: 'flex' }, marginRight: 0 }}>
							{pages.map((page, index) => (
								<React.Fragment key={page.key}>
									{index !== 0 && <Separator>|</Separator>}
									<MenuButton
										href={page.path}
										color='inherit'
										size='medium'
										onClick={() => {
											navigate(page.path);
										}}
									>
										<Trans i18nKey={page.key} />
									</MenuButton>
								</React.Fragment>
							))}

							<Box sx={{ ml: 2.5 }}>
								<PlayInAppButton size='medium' onClick={downloadApp} />
							</Box>
						</Box>
					</HeaderToolbar>
				</Container>
			</Wrapper>
		</>
	);
};
